import { API_auth_logout } from '@core/api/paths';
import { TAnyFunction } from '@core/helpers';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { clearUserAuth, getAuthHeaders, userAuthHasExpired } from 'src/app/core/http/user-auth';
import { getFeatureFlag } from 'src/app/core/services/feature-flag-helpers';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import {
  captureSlowWarning,
  logErrorInSentry,
} from 'src/app/project/modules/errors/response-error';
import { logout$ } from '../modules/auth/logout.service';
import { EAuthRoute } from './constants/auth.constants';

const retryCounts = new WeakMap<RequestInit, number>();
let globalAuthFailureCount = 0;
export const AUTH_MAX_RETRIES = 1;
export const RETRY_MAX_ATTEMPTS = 3;
export const RETRY_BASE_DELAY_MS = 250;

const NON_RETRYABLE_STATUSES: ReadonlySet<number> = new Set([
  EStatusCode.BAD_REQUEST,
  EStatusCode.FORBIDDEN,
  EStatusCode.NOT_FOUND,
]);

const RETRYABLE_METHODS: ReadonlySet<string> = new Set(['GET', 'PUT', 'DELETE', 'HEAD']);

const RATE_LIMIT_BASE_DELAY_MS = 1000;

export const getRetryDelayMs = (attemptIndex: number, status: number): number => {
  const baseDelay =
    status === EStatusCode.TOO_MANY_REQUESTS ? RATE_LIMIT_BASE_DELAY_MS : RETRY_BASE_DELAY_MS;

  return baseDelay * Math.pow(2, attemptIndex);
};

const sleep = (ms: number, signal?: AbortSignal | null): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      // cancel timeout if api is already aborted
      reject(new DOMException('Aborted', 'AbortError'));

      return;
    }

    const timer = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        },
        { once: true },
      );
    }
  });

export type TFetchObject = Promise<unknown>;

let requests: TRequests = {};

export type TRequests = {
  [workspaceId: string]: {
    controller: AbortController;
    status: 'fetching' | 'fetched' | 'error';
    queue: TAnyFunction[];
    promise: Promise<unknown>;
  };
};

const logoutEndpoint = `POST|${API_auth_logout()}`;

export const fetchFile = (path: string): Promise<Response> =>
  fetch(path, {
    headers: {
      ...getAuthHeaders(),
    },
  });

const isOnAuthenticatedRoute = (url: string): boolean =>
  url.indexOf(EAuthRoute.LOGIN) === -1 &&
  url.indexOf('passwords') === -1 &&
  url.indexOf(EAuthRoute.SIGNUP) === -1 &&
  url.indexOf(EAuthRoute.TWO_FACTOR_AUTHENTICATION_SETUP) === -1 &&
  url.indexOf(EAuthRoute.TWO_FACTOR_AUTHENTICATION_LOGIN) === -1;

export const shouldRetry = (status: number, method: string): boolean => {
  if (!getFeatureFlag(EFeatureFlag.RETRY_API_LOGIC)) {
    return false;
  }

  if (!RETRYABLE_METHODS.has(method)) {
    return false;
  }

  if (status === EStatusCode.UNAUTHORIZED) {
    return isOnAuthenticatedRoute(window.location.href);
  }

  if (NON_RETRYABLE_STATUSES.has(status) || (method === 'DELETE' && status >= 500)) {
    return false;
  }

  return (
    status >= 500 ||
    status === EStatusCode.REQUEST_TIMEOUT ||
    status === EStatusCode.TOO_MANY_REQUESTS
  );
};

const getMaxRetries = (status: number): number =>
  status === EStatusCode.UNAUTHORIZED ? AUTH_MAX_RETRIES : RETRY_MAX_ATTEMPTS;

const triggerRetry = async ({
  url,
  fetchOptions,
  res,
}: {
  url: string;
  fetchOptions: RequestInit;
  res: Response;
}): Promise<unknown> => {
  const currentCount = retryCounts.get(fetchOptions) ?? 0;
  const isUnauthorised = res.status === EStatusCode.UNAUTHORIZED;
  const maxRetries = getMaxRetries(res.status);

  retryCounts.set(fetchOptions, currentCount + 1);

  if (isUnauthorised) {
    globalAuthFailureCount++;
  }

  if (
    currentCount >= maxRetries ||
    (isUnauthorised && globalAuthFailureCount >= RETRY_MAX_ATTEMPTS)
  ) {
    if (isUnauthorised) {
      logout$.next();
    }

    return Promise.reject(res);
  }

  if (isUnauthorised && userAuthHasExpired()) {
    clearUserAuth();
    logout$.next();

    return Promise.reject(res);
  }

  await sleep(getRetryDelayMs(currentCount, res.status), fetchOptions.signal);

  return fetchAPI(url, fetchOptions);
};

const callerSignals = new WeakMap<RequestInit, AbortSignal | null>();

const checkForAbortedRequest = (
  callerSignal: AbortSignal | null | undefined,
  controller: AbortController,
): void => {
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
};

const fetchAPI = (_url: string, options: RequestInit): TFetchObject => {
  const _options = options;

  if (!callerSignals.has(_options)) {
    callerSignals.set(_options, _options.signal ?? null);
  }

  const endpoint = `${_options.method}|${_url}`;
  const controller = new AbortController();
  const signal = controller.signal;

  const callerSignal = callerSignals.get(_options);
  checkForAbortedRequest(callerSignal, controller);

  _options.signal = signal;
  const startTime = Date.now();

  const promise = (): Promise<any> =>
    new Promise((resolve, reject) => {
      fetch(_url, _options)
        .then(async (_response) => {
          if (_response.ok) {
            retryCounts.delete(_options);
            globalAuthFailureCount = 0;
          }

          if (requests[endpoint]?.queue?.length) {
            const nextPromise = requests[endpoint].queue.shift();

            requests[endpoint].promise = nextPromise();
          } else if (_options.method === 'GET') {
            if (_response.ok) {
              requests[endpoint].controller = null;
              requests[endpoint].status = 'fetched';
            } else if (requests[endpoint]) {
              requests[endpoint].controller = null;
              requests[endpoint].status = 'error';
            }
          } else {
            delete requests[endpoint];
          }

          if (!_response.ok) {
            if (shouldRetry(_response.status, _options.method)) {
              // Keep the GET entry in 'fetching' state so concurrent callers during the
              // retry sleep dedupe onto the current promise rather than spawning a parallel fetch.
              if (_options.method === 'GET' && requests[endpoint]) {
                requests[endpoint].status = 'fetching';
              }

              try {
                const retryResult = await triggerRetry({
                  url: _url,
                  fetchOptions: _options,
                  res: _response,
                });

                resolve(retryResult);
              } catch (retryError) {
                reject(retryError);
              }

              return;
            } else {
              if (
                _response.status === EStatusCode.UNAUTHORIZED &&
                isOnAuthenticatedRoute(window.location.href)
              ) {
                logout$.next();
                reject(_response);
              }

              reject(_response);

              throw new Error(_response.status + '');
            }
          }

          if (_response.headers.get('Content-type') === null) {
            resolve(null);
          } else {
            try {
              const data = await _response.json();

              const completedTime = Date.now();
              const elapsedTime = completedTime - startTime;
              const fiftySeconds = 50000;

              if (elapsedTime > fiftySeconds) {
                console.warn(`SLOW API: ${_url}`);
                captureSlowWarning({ message: `SLOW API: ${_url}` });
              }

              resolve(data);
            } catch (error) {
              resolve(null);
              logErrorInSentry({ error, message: 'Failed to parse fetch response as JSON' });
            }
          }
        })
        .catch((error) => {
          delete requests[endpoint];
          reject(error);
        });
    });

  // TODO Remove the "if request is queued return in in the next call even if body is wrong". Or switch to Angular HTTP for all requests
  if (_options.method === 'POST' || _options.method === 'PUT' || _options.method === 'DELETE') {
    return promise();
  } else if (requests[endpoint]) {
    if (_options.method === 'GET') {
      requests[endpoint].promise = promise();
      requests[endpoint].status = 'fetching';
      requests[endpoint].controller = controller;
    } else {
      requests[endpoint].queue.push(promise);
    }
  } else {
    requests[endpoint] = {
      promise: promise(),
      status: 'fetching',
      controller,
      queue: [],
    };
  }

  return requests[endpoint].promise;
};

export const fetchGet = (_url: string): TFetchObject => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
  };

  const endpoint = `${options.method}|${_url}`;

  if (
    requests[endpoint] &&
    (!requests[endpoint].controller ||
      (requests[endpoint].controller && !requests[endpoint].controller.signal.aborted))
  ) {
    if (requests[endpoint].promise && requests[endpoint].status === 'fetching') {
      return requests[endpoint].promise;
    }
  }

  return fetchAPI(_url, options);
};

export const fetchPost = (_url: string, _body: unknown): TFetchObject => {
  const options: RequestInit = {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    }),
  };

  if (_body) {
    if (typeof _body === 'string') {
      options.body = _body;
    } else {
      options.body = JSON.stringify(_body);
    }
  }

  return fetchAPI(_url, options);
};

export const fetchPostWithFormData = (url: string, body: FormData): TFetchObject => {
  const options: RequestInit = {
    method: 'POST',
    body,
    headers: new Headers({
      ...getAuthHeaders(),
    }),
  };

  return fetchAPI(url, options);
};

export const fetchPut = (_url: string, _body: unknown): TFetchObject => {
  const options: RequestInit = {
    method: 'PUT',
    headers: new Headers({
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    }),
  };

  if (_body) {
    if (typeof _body === 'string') {
      options.body = _body;
    } else {
      options.body = JSON.stringify(_body);
    }
  }

  return fetchAPI(_url, options);
};

export const fetchDelete = (_url: string, body?: unknown): TFetchObject => {
  const options: RequestInit = {
    method: 'DELETE',
    headers: new Headers({
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    }),
  };

  if (body) {
    if (typeof body === 'string') {
      options.body = body;
    } else {
      options.body = JSON.stringify(body);
    }
  }

  return fetchAPI(_url, options);
};

export function clearRequestsData(isLogout: boolean = false): void {
  let logoutRequest;
  Object.keys(requests).forEach((requestId) => {
    const request = requests[requestId];

    if (isLogout && requestId === logoutEndpoint) {
      logoutRequest = request;

      return;
    }

    if (request.controller) {
      request.controller.abort();
    }
  });

  requests = {};
  globalAuthFailureCount = 0;

  if (logoutRequest) {
    requests[logoutEndpoint] = logoutRequest;
  }
}
